import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';
import useSEO from '../hooks/useSEO';
import { VIDEO } from '../data/media';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';
import Marquee from '../components/Marquee';

function BrandPortal({ brand, img, pos = 'center', tag, desc, cta, to, index, testId }) {
  const { t } = useSite();
  const color = brand === 'media' ? '#14DA64' : '#0CBEDD';
  return (
    <Link
      to={to}
      data-testid={testId}
      data-cursor={`${t.cursor.explore} →`}
      className="group relative flex min-h-[70vh] flex-col justify-end overflow-hidden p-8 sm:p-14"
    >
      <img
        src={img}
        alt={brand === 'media' ? 'F&L Media' : 'F&L Souvenir'}
        loading="lazy"
        style={{ objectPosition: pos }}
        className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
      <div
        className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        style={{ background: `radial-gradient(ellipse at bottom, ${color}26, transparent 60%)` }}
      />
      <div className="relative z-10">
        <span className="font-mono text-xs tracking-[0.3em] text-zinc-500">{String(index + 1).padStart(2, '0')}</span>
        <h3
          className="mt-3 font-heading text-5xl font-black uppercase tracking-tight transition-colors duration-500 sm:text-6xl"
          style={{ color: '#fff' }}
        >
          F&L <span style={{ color }}>{brand === 'media' ? 'MEDIA' : 'SOUVENIR'}</span>
        </h3>
        <p className="mt-3 font-heading text-lg font-bold uppercase tracking-wide text-white">{tag}</p>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-400">{desc}</p>
        <span
          className="mt-6 inline-flex items-center gap-2 border px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-all duration-500 group-hover:gap-4"
          style={{ borderColor: color }}
        >
          {cta}
          <ArrowUpRight className="h-4 w-4" style={{ color }} />
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { lang, t } = useSite();
  useSEO(t.meta.home, 'home', lang);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const videoY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const stats = [
    { value: '04', label: t.home.statYears },
    { value: '02', label: t.home.statBrands },
    { value: '04', label: t.home.statAreas },
    { value: '01', label: t.home.statVision },
  ];

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative flex h-[100svh] flex-col justify-end overflow-hidden">
        <motion.div style={{ y: videoY }} className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/assets/hero-poster.jpg"
            className="h-full w-full object-cover opacity-50"
            data-testid="hero-video"
          >
            <source src={VIDEO.hero} type="video/mp4" media="(min-width: 1280px)" />
            <source src="/assets/hero-small.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/70" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-16 sm:px-8 sm:pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}>
            <Kicker>{t.home.kicker}</Kicker>
          </motion.div>
          <h1 className="mt-6 font-heading text-[7.5vw] font-black uppercase leading-[1.02] tracking-tight sm:text-7xl lg:text-8xl">
            <MaskedLines
              delay={0.4}
              lines={[
                t.home.titleA,
                t.home.titleB,
                <span key="c" className="brand-text">{t.home.titleC}</span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-base text-zinc-400 sm:text-lg"
          >
            {t.home.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500"
          >
            {t.home.scroll}
            <ArrowDown className="h-4 w-4 animate-bounce brand-text" />
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={t.home.marquee} />

      <section className="grid lg:grid-cols-2" data-testid="brand-portals">
        <BrandPortal
          brand="media"
          img="/assets/media-portal.webp"
          tag={t.home.media.tag}
          desc={t.home.media.desc}
          cta={t.home.media.cta}
          to={pathFor('media', lang)}
          index={0}
          testId="portal-media"
        />
        <BrandPortal
          brand="souvenir"
          img="/assets/souvenir-staff.webp"
          pos="center 55%"
          tag={t.home.souvenir.tag}
          desc={t.home.souvenir.desc}
          cta={t.home.souvenir.cta}
          to={pathFor('souvenir', lang)}
          index={1}
          testId="portal-souvenir"
        />
      </section>

      <section className="mx-auto max-w-[1600px] px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Kicker>{t.home.groupKicker}</Kicker>
            <h2 className="mt-6 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-6xl">
              {t.home.groupTitle}
            </h2>
            <p className="mt-6 max-w-lg leading-relaxed text-zinc-400">{t.home.groupText}</p>
            <Link
              to={pathFor('about', lang)}
              data-testid="home-about-cta"
              data-cursor={t.cursor.open}
              className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors brand-text hover:opacity-80"
            >
              {t.home.aboutCta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-px bg-zinc-900">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="bg-black">
                <div className="flex h-full flex-col justify-between gap-6 p-8 sm:p-10">
                  <span className="h-1.5 w-1.5 rounded-full brand-bg" />
                  <div>
                    <p className="font-heading text-5xl font-black tracking-tight sm:text-6xl">{s.value}</p>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{s.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900" data-testid="home-b2b2c">
        <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-8 sm:py-32">
          <Reveal className="mb-14">
            <Kicker>{t.home.b2b2cKicker}</Kicker>
            <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.home.b2b2cTitle}
            </h2>
          </Reveal>
          <div className="grid gap-px bg-zinc-900 lg:grid-cols-2">
            {[
              { title: t.home.b2bTitle, text: t.home.b2bText, id: 'b2b' },
              { title: t.home.b2cTitle, text: t.home.b2cText, id: 'b2c' },
            ].map((col, i) => (
              <Reveal key={col.id} delay={i * 0.12} className="bg-black">
                <div className="group flex h-full flex-col justify-between gap-10 p-8 transition-colors duration-500 hover:bg-zinc-950 sm:p-12" data-testid={`home-${col.id}`}>
                  <span className="font-heading text-6xl font-black text-stroke transition-colors duration-500 group-hover:text-[var(--brand)] group-hover:[-webkit-text-stroke:0px] sm:text-7xl">
                    {col.id === 'b2b' ? 'B2B' : 'B2C'}
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-white">{col.title}</h3>
                    <p className="mt-3 max-w-md leading-relaxed text-zinc-400">{col.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900" data-testid="home-contact">
        <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-8 sm:py-32">
          <Reveal className="mb-14">
            <Kicker>{t.home.contactKicker}</Kicker>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-6xl">
              {t.home.contactTitle}
            </h2>
          </Reveal>
          <div className="grid gap-px bg-zinc-900 lg:grid-cols-2">
            {[
              {
                id: 'media',
                color: '#14DA64',
                brand: t.home.contactMediaBrand,
                title: t.home.contactMediaTitle,
                text: t.home.contactMediaText,
              },
              {
                id: 'souvenir',
                color: '#0CBEDD',
                brand: t.home.contactSouvenirBrand,
                title: t.home.contactSouvenirTitle,
                text: t.home.contactSouvenirText,
              },
            ].map((card, i) => (
              <Reveal key={card.id} delay={i * 0.12} className="bg-black">
                <Link
                  to={`${pathFor('contact', lang)}?marca=${card.id}`}
                  data-testid={`home-contact-${card.id}`}
                  data-cursor={t.cursor.contact}
                  className="group flex h-full flex-col justify-between gap-12 p-8 transition-colors duration-500 hover:bg-zinc-950 sm:p-12"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="font-mono text-[11px] uppercase tracking-[0.3em]"
                      style={{ color: card.color }}
                    >
                      {card.brand}
                    </span>
                    <ArrowUpRight
                      className="h-6 w-6 text-zinc-600 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
                      style={{ transitionProperty: 'transform, color' }}
                    />
                  </div>
                  <div>
                    <h3 className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                      {card.title}
                    </h3>
                    <p className="mt-3 max-w-md leading-relaxed text-zinc-400">{card.text}</p>
                    <span
                      className="mt-6 block h-px w-full origin-left scale-x-0 transition-transform duration-700 ease-out group-hover:scale-x-100"
                      style={{ backgroundColor: card.color }}
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
